// Bộ nhận diện dùng chung cho mọi màn CR & hiệu quả chăm sóc (plan §3.2).
//
// Một nguồn duy nhất cho: chặng phễu V/R/P/T/C/O/Q/F, thứ tự 8 tỷ lệ, ngưỡng RAG,
// nhãn thực thi store (A/B/C) và nhãn tin cậy nguồn dữ liệu. Không TSX nào được
// hard-code ngưỡng: mọi màn gọi `rag()`/`tier()` với thresholds từ API, chỉ rơi về
// bảng fallback dưới đây khi API (màn cũ) chưa trả `thresholds`.
import type { Tone } from '../types';
import { colorStatus, statusLabel } from '../utils';

// ═══════════════ Chặng phễu chuẩn ═══════════════
export type StageCode = 'V' | 'R' | 'P' | 'T' | 'C' | 'O' | 'Q' | 'F';

export interface StageDef {
  code: StageCode;
  metric: string; // tên cột trong care_cr_store_daily_v
  label: string;
  tone: Tone;
  note?: string;
}

export const STAGES: StageDef[] = [
  { code: 'V', metric: 'visits', label: 'Lượt khách', tone: 'cyan', note: 'Palexy theo store×ngày' },
  { code: 'R', metric: 'captures', label: 'Ghi nhận', tone: 'blue', note: 'capture telegram_agent' },
  { code: 'P', metric: 'with_phone', label: 'Có SĐT', tone: 'teal' },
  { code: 'T', metric: 'interacted', label: 'Tương tác', tone: 'violet', note: 'khớp SĐT + shop trả lời ≤7 ngày' },
  { code: 'C', metric: 'closed_tag', label: 'Chốt tự báo', tone: 'gray-outline', note: 'tag tự khai — không phải đơn thật' },
  { code: 'O', metric: 'orders_verified', label: 'Đơn xác minh', tone: 'green', note: 'f_sales ≤30 ngày (gồm đơn cùng ngày)' },
  { code: 'Q', metric: 'instore_sales', label: 'Bán tại quầy', tone: 'indigo' },
  { code: 'F', metric: 'fsales_orders', label: 'Đơn cửa hàng', tone: 'navy', note: 'f_sales theo store×ngày' },
];

export const STAGE_BY_CODE = Object.fromEntries(STAGES.map(stage => [stage.code, stage])) as Record<StageCode, StageDef>;

// Tone cố định cho stage tag — C (tự báo) KHÔNG bao giờ green.
export const STAGE_TONE: Record<StageCode, Tone> = Object.fromEntries(
  STAGES.map(stage => [stage.code, stage.tone]),
) as Record<StageCode, Tone>;

// ═══════════════ 8 tỷ lệ chuẩn ═══════════════
export type RateKey = 'coverage' | 'contact' | 'interaction' | 'close_tag' | 'cr_care' | 'cr_instore' | 'cr_store' | 'verify_gap';

export interface RateDef {
  key: RateKey;
  label: string;
  short: string;
  formula: string;
}

export const RATES: RateDef[] = [
  { key: 'coverage', label: 'Độ phủ ghi nhận', short: 'Phủ', formula: 'R/V' },
  { key: 'contact', label: 'Xin được SĐT', short: 'SĐT', formula: 'P/R' },
  { key: 'interaction', label: 'Tương tác Vpage', short: 'Tương tác', formula: 'T/P' },
  { key: 'close_tag', label: 'Chốt tự báo', short: 'Tự báo', formula: 'C/R' },
  { key: 'cr_care', label: 'CR chăm sóc (đơn sau ≥ D+1)', short: 'CR chăm', formula: 'O≥D+1/P' },
  { key: 'cr_instore', label: 'CR tại quầy', short: 'CR quầy', formula: 'Q/V' },
  { key: 'cr_store', label: 'CR cửa hàng', short: 'CR store', formula: 'F.orders/V' },
  { key: 'verify_gap', label: 'Lệch tự báo', short: 'Lệch', formula: '(C−O)/C' },
];

export const RATE_KEYS: RateKey[] = RATES.map(rate => rate.key);
export const RATE_BY_KEY = Object.fromEntries(RATES.map(rate => [rate.key, rate])) as Record<RateKey, RateDef>;

// ═══════════════ Ngưỡng RAG ═══════════════
export interface Threshold {
  metric: string;
  label: string;
  green: number;
  amber: number;
  direction: 'higher' | 'lower';
}

export type RagTone = 'green' | 'amber' | 'red' | 'gray';

// Fallback khi API chưa trả `thresholds`: tái tạo ĐÚNG ngưỡng đang hard-code ở 4 màn cũ,
// nên số và màu hiện tại không đổi. Thay bằng bảng `care_kpi_threshold` khi API có.
export const LEGACY_THRESHOLDS: Threshold[] = [
  { metric: 'coverage', label: 'Độ phủ ghi nhận', green: 6, amber: 3, direction: 'higher' },
  { metric: 'sdt_visit', label: 'Xin SĐT / lượt khách', green: 20, amber: 10, direction: 'higher' },
  { metric: 'close_rate', label: 'Chốt / mua hàng', green: 50, amber: 30, direction: 'higher' },
  // Funnel chỉ có hai mức green/red (≥30% đạt) → amber đặt bằng green để không sinh màu mới.
  { metric: 'contact', label: 'Xin SĐT / ghi nhận', green: 30, amber: 30, direction: 'higher' },
];

// Seed care_kpi_threshold sau review WP3 — dùng khi màn CR chưa đọc được bảng.
// cr_care tính theo orders_followup (đơn ≥ D+1) / có SĐT; interaction chỉ có số khi ≥ 20 SĐT khớp Vpage.
export const CR_THRESHOLDS: Threshold[] = [
  { metric: 'coverage', label: 'Độ phủ ghi nhận', green: 25, amber: 15, direction: 'higher' },
  { metric: 'contact', label: 'Xin được SĐT', green: 65, amber: 50, direction: 'higher' },
  { metric: 'interaction', label: 'Tương tác Vpage', green: 8, amber: 3, direction: 'higher' },
  { metric: 'cr_care', label: 'CR chăm sóc (đơn sau ≥ D+1)', green: 3, amber: 1, direction: 'higher' },
  { metric: 'cr_instore', label: 'CR tại quầy', green: 8, amber: 4, direction: 'higher' },
  { metric: 'verify_gap', label: 'Khoảng cách tự báo − xác minh ((chốt tag − đơn xác minh) / chốt tag)', green: 20, amber: 40, direction: 'lower' },
];
// Dưới mốc này API trả interaction = null (khớp functions/api/care/cr.js MIN_VPAGE_MATCH) → badge no_data.
export const CR_MIN_VPAGE_MATCH = 20;

// RAG một chỉ số theo ngưỡng DB. Thiếu ngưỡng hoặc thiếu giá trị → 'gray' (không xếp loại).
export function rag(metric: string, value: number | null | undefined, thresholds?: Threshold[] | null): RagTone {
  if (value == null || !Number.isFinite(Number(value))) return 'gray';
  const t = (thresholds || []).find(item => item.metric === metric);
  if (!t) return 'gray';
  const v = Number(value);
  if (t.direction === 'lower') return v <= t.green ? 'green' : v <= t.amber ? 'amber' : 'red';
  return v >= t.green ? 'green' : v >= t.amber ? 'amber' : 'red';
}

// ═══════════════ Nhãn thực thi store (A/B/C) ═══════════════
// A = coverage green VÀ contact green VÀ interaction ≥ amber · C = bất kỳ chỉ số red · còn lại B.
export function tier(rates: Partial<Record<RateKey, number | null>> | null | undefined, thresholds?: Threshold[] | null): 'A' | 'B' | 'C' {
  const tone = (metric: RateKey) => rag(metric, rates?.[metric] ?? null, thresholds);
  if (RATE_KEYS.some(key => tone(key) === 'red')) return 'C';
  if (tone('coverage') === 'green' && tone('contact') === 'green' && tone('interaction') !== 'red') return 'A';
  return 'B';
}

export const tierTone = (value: string | null | undefined): Tone =>
  value === 'A' ? 'green' : value === 'B' ? 'amber' : value === 'C' ? 'red' : 'gray';

export const tierLabel = (value: string | null | undefined): string =>
  value === 'A' ? 'Thực thi tốt' : value === 'B' ? 'Trung bình' : value === 'C' ? 'Cần chấn chỉnh' : 'Chưa xếp loại';

// ═══════════════ Delta & nhãn tin cậy nguồn ═══════════════
// pp = điểm phần trăm so với kỳ trước. null → '–' (không có kỳ trước để so).
export function formatDelta(pp: number | null | undefined): string {
  if (pp == null || !Number.isFinite(Number(pp))) return '–';
  const n = Number(pp);
  const abs = Math.abs(n).toFixed(1).replace('.', ',');
  if (Math.abs(n) < 0.05) return `= 0,0 điểm`;
  return `${n > 0 ? '▲' : '▼'} ${abs} điểm`;
}

export const deltaTone = (pp: number | null | undefined): Tone =>
  pp == null || !Number.isFinite(Number(pp)) ? 'gray' : Number(pp) > 0 ? 'green' : Number(pp) < 0 ? 'red' : 'gray';

export type SourceStatus = 'verified' | 'partial' | 'no_data';

export interface SourceBadge {
  label: string;
  tone: Tone;
  note: string;
}

export function sourceBadge(status: string | null | undefined): SourceBadge {
  if (status === 'verified') return { label: 'Xác minh', tone: 'green', note: 'có f_sales + Palexy trong kỳ' };
  if (status === 'partial') return { label: 'Thiếu nguồn', tone: 'amber', note: 'thiếu 1 nguồn trong kỳ' };
  return { label: 'Không có dữ liệu', tone: 'gray-outline', note: 'nguồn trắng hoặc chưa nuôi' };
}

// ═══════════════ Trạng thái chăm sóc (màn cũ) ═══════════════
// `closed` = tag tự báo → tone tự báo (gray-outline), KHÔNG green; các trạng thái khác
// giữ nguyên màu như `colorStatus` trước đây để 4 màn cũ không đổi màu ngoài `closed`.
export function careStatusTone(status?: string | null): Tone {
  if (status === 'closed') return 'gray-outline';
  return colorStatus(statusLabel(status));
}

export const careStatusLabel = (status?: string | null): string => statusLabel(status);

// ═══════════════ Tone kế thừa cho 4 màn cũ (fallback = ngưỡng hiện tại) ═══════════════
export const coverTone = (pct: number, thresholds?: Threshold[] | null): RagTone =>
  rag('coverage', pct, thresholds || LEGACY_THRESHOLDS);

export const sdtTone = (pct: number, thresholds?: Threshold[] | null): RagTone =>
  rag('sdt_visit', pct, thresholds || LEGACY_THRESHOLDS);

export const crTone = (pct: number, thresholds?: Threshold[] | null): RagTone =>
  rag('close_rate', pct, thresholds || LEGACY_THRESHOLDS);

export const rateTone = (pct: number, thresholds?: Threshold[] | null): RagTone =>
  rag('close_rate', pct, thresholds || LEGACY_THRESHOLDS);

export const contactTone = (pct: number, thresholds?: Threshold[] | null): RagTone =>
  rag('contact', pct, thresholds || LEGACY_THRESHOLDS);

export const ageTone = (days: number): Tone => (days >= 3 ? 'red' : days >= 1 ? 'amber' : 'gray');

// ═══════════════ Phân loại phễu cũ (FunnelScreen) — giữ nguyên id/nhãn/tone ═══════════════
export interface FunnelStageDef {
  id: string;
  label: string;
  tone: Tone;
  color: string;
}

export const FUNNEL_STAGES: FunnelStageDef[] = [
  { id: 'instore_no_contact', label: 'Mua tại quầy - thiếu SĐT', tone: 'red', color: '#d71920' },
  { id: 'instore_with_contact', label: 'Mua tại quầy - có SĐT', tone: 'green', color: '#059669' },
  { id: 'lead_no_phone', label: 'Lead không mua - thiếu SĐT', tone: 'amber', color: '#d97706' },
  { id: 'lead_with_phone', label: 'Lead có SĐT - follow', tone: 'blue', color: '#2563eb' },
  { id: 'converted_30d', label: 'Có đơn ≤30d (gồm cùng ngày)', tone: 'green', color: '#16a34a' },
  { id: 'review', label: 'Cần review', tone: 'gray', color: '#64748b' },
];

export const FUNNEL_SUPPORT: Array<{ id: string; label: string; tone: Tone }> = [
  { id: 'pos_missing_contact', label: 'POS thiếu SĐT', tone: 'red' },
  { id: 'post_sale_care', label: 'Chăm sau bán', tone: 'green' },
  { id: 'need_contact', label: 'Cần xin SĐT', tone: 'amber' },
  { id: 'ready_followup', label: 'Sẵn follow', tone: 'blue' },
  { id: 'converted', label: 'Có đơn ≤30d (gồm cùng ngày)', tone: 'green' },
  { id: 'review', label: 'Cần review', tone: 'gray' },
];

// ═══════════════ Chú thích vùng trắng Vpage ═══════════════
export const VPAGE_GAP_START = '2026-07-16';
export const VPAGE_GAP_END = '2026-10-08';
export const VPAGE_GAP_NOTE = `Vpage không có dữ liệu ${VPAGE_GAP_START.slice(8)}/${VPAGE_GAP_START.slice(5, 7)}–${VPAGE_GAP_END.slice(8)}/${VPAGE_GAP_END.slice(5, 7)}`;

export const isVpageGap = (ngay: string): boolean => ngay >= VPAGE_GAP_START && ngay <= VPAGE_GAP_END;
